import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CalendarClock, Pause, Play, Plus } from 'lucide-react'
import { amortizePrepaidOne, amortizePrepaidsDue, getPrepaids, updatePrepaidStatus } from '../../api/operatingCosts'
import { accountLabel, useAccounts } from '../../components/AccountSelect'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import AccountingTabs from './AccountingTabs'
import { monthName } from './accountingShared'

const SHOW = [
  { id: 'active', label: 'Active' },
  { id: 'due', label: 'To amortize' },
  { id: 'paused', label: 'Paused' },
  { id: 'completed', label: 'Completed' },
  { id: 'all', label: 'All' },
]
const STATUS_TONE = { Active: 'good', Paused: 'warn', Completed: 'muted' }

// Costs paid in advance, moved into expenses a month at a time.
export default function PrepaidList() {
  const { notify } = useToast()
  const accounts = useAccounts()
  const [params, setParams] = useSearchParams()
  const show = SHOW.some((s) => s.id === params.get('show')) ? params.get('show') : 'active'
  const [rows, setRows] = useState(null)
  const [open, setOpen] = useState(null)
  const [confirmAll, setConfirmAll] = useState(false)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  useEffect(() => { getPrepaids().then((d) => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])) }, [version])

  // A finished schedule counts as completed even before its status says so.
  const state = (r) => (r.status === 'Completed' || num(r.remaining) <= 0 ? 'completed' : String(r.status || 'Active').toLowerCase())
  const inShow = (r, id) => id === 'all' || (id === 'due' ? r.due : state(r) === id)
  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, (rows || []).filter((r) => inShow(r, s.id)).length])), [rows])
  const shown = (rows || []).filter((r) => inShow(r, show))
  const dueTotal = (rows || []).filter((r) => r.due).length

  const run = async (key, fn, message) => {
    setBusy(key)
    setError('')
    try {
      const res = await fn()
      notify(typeof message === 'function' ? message(res) : message)
      setConfirmAll(false)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'That did not work.')
    } finally {
      setBusy('')
    }
  }
  // "Amortized 3 months", and why it stopped early (a closed month).
  const postedMsg = (n, stopped) => `Amortized ${n} ${n === 1 ? 'month' : 'months'}${stopped?.length ? `. Stopped: ${stopped.join('; ')}. Reopen the month under Month-end close to post it.` : ''}`

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Prepaids <HelpTip k="prepaid" /></h1>
            <div className="ci-title-meta">Costs paid in advance, expensed month by month.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {dueTotal > 0 && <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setConfirmAll(true)}><CalendarClock size={15} />Amortize what’s due</button>}
          <Link data-write className="ci-btn ci-btn-primary" to="/accounting/prepaids/new"><Plus size={15} />Add a prepaid</Link>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <FilterChips options={SHOW.map((s) => ({ ...s, count: counts[s.id] }))} value={show} onChange={(id) => setParams(id === 'active' ? {} : { show: id }, { replace: true })} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Prepaid</th>
                <th className="num hide-sm">Total</th>
                <th className="num hide-sm">Per month</th>
                <th className="num">Left to expense</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan="5"><Spinner label="Loading prepaids…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">{rows.length ? 'Nothing here.' : 'No prepaids yet.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.prepaid_id} className="row-link" onClick={() => setOpen(r)}>
                  <td>
                    <button type="button" className="acc-link-btn acc-row-btn" onClick={(e) => { e.stopPropagation(); setOpen(r) }}>{r.name}</button>
                    <span className="cell-sub">{monthName(r.start_date?.slice(0, 7))} to {monthName(r.end_ym)} · {r.months_posted} of {r.months} months</span>
                  </td>
                  <td className="num hide-sm">{money(r.amount_total)}</td>
                  <td className="num hide-sm">{money(r.per_month)}</td>
                  <td className="num ci-total">{money(r.remaining)}</td>
                  <td className="center">
                    <StatusBadge label={state(r) === 'completed' ? 'Completed' : (r.status || 'Active')} tone={STATUS_TONE[state(r) === 'completed' ? 'Completed' : r.status] || 'good'} />
                    {r.due && <span className="cell-sub acc-due">{monthName(r.next_ym)} due</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'prepaid' : 'prepaids'} · {money(shown.reduce((s, r) => s + num(r.remaining), 0))} left to expense</span>
        </div>
      </div>

      <Modal
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title={<>Amortize what’s due <HelpTip k="amortization" /></>}
        subtitle={`${dueTotal} ${dueTotal === 1 ? 'prepaid has' : 'prepaids have'} months due up to this one. Each month is posted as a journal entry.`}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmAll(false)}>Cancel</Button>
            <Button variant="primary" loading={busy === 'all'} onClick={() => run('all', amortizePrepaidsDue, (res) => postedMsg((res?.results || []).reduce((s, x) => s + (x.posted || 0), 0), res?.stopped))}>Amortize</Button>
          </>
        )}
      />

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.name}
        subtitle={open && `${money(open.amount_total)} from ${monthName(open.start_date?.slice(0, 7))}, ${money(open.per_month)} a month for ${open.months} months.`}
        footer={open && (
          <>
            {open.status === 'Active' && state(open) !== 'completed' && <Button data-write variant="ghost" loading={busy === 'pause'} onClick={() => run('pause', () => updatePrepaidStatus(open.prepaid_id, 'Paused'), 'Paused').then(() => setOpen(null))}><Pause size={14} />Pause</Button>}
            {open.status === 'Paused' && <Button data-write variant="ghost" loading={busy === 'resume'} onClick={() => run('resume', () => updatePrepaidStatus(open.prepaid_id, 'Active'), 'Resumed').then(() => setOpen(null))}><Play size={14} />Resume</Button>}
            {open.due && <Button data-write variant="primary" loading={busy === 'one'} onClick={() => run('one', () => amortizePrepaidOne(open.prepaid_id), (res) => postedMsg(res?.posted || 0, res?.stopped ? [res.stopped] : [])).then(() => setOpen(null))}>Amortize {monthName(open.next_ym, { year: false })}</Button>}
            {!open.due && <Button variant="ghost" onClick={() => setOpen(null)}>Close</Button>}
          </>
        )}
      >
        {open && (
          <dl className="acc-details acc-details-tight">
            <div><dt>Expensed so far</dt><dd>{money(open.amortized)} · {open.months_posted} of {open.months} months</dd></div>
            <div><dt>Left to expense</dt><dd>{money(open.remaining)}</dd></div>
            <div><dt>Next month</dt><dd>{open.next_ym ? monthName(open.next_ym) : 'None, it’s finished'}</dd></div>
            <div><dt>Prepaid account <HelpTip k="prepaidAccount" /></dt><dd>{accountLabel(accounts, open.prepaid_account_code)}</dd></div>
            <div><dt>Expense account <HelpTip k="prepaidExpenseAccount" /></dt><dd>{accountLabel(accounts, open.expense_account_code)}</dd></div>
            <div><dt>Payment entry</dt><dd>{open.je_initial_id ? <Link to={`/accounting/journal/${open.je_initial_id}`}>{docNo('je', open.je_initial_no) || 'Open'}</Link> : 'Not posted here'}</dd></div>
            <div><dt>Started</dt><dd>{fmtDay(open.start_date)}</dd></div>
          </dl>
        )}
      </Modal>
    </div>
  )
}
